import { useEffect, useMemo, useState } from 'react';
import { MediaItem } from '../@types/Media';
import { apiClient } from '../api/client';
import { getMimeTypeIcon } from '../utils/mimeIcons';

interface MediaPreviewProps {
  item: MediaItem;
  width?: string | number;
  height?: string | number;
  controls?: boolean;
}

const MediaPreview = ({ item, width = '100%', height, controls = true }: MediaPreviewProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    let urlToRevoke: string | null = null;

    apiClient
      .getMediaFileBlob(item.id)
      .then((blob) => {
        if (canceled) return;
        urlToRevoke = URL.createObjectURL(blob);
        setObjectUrl(urlToRevoke);
      })
      .catch(() => {
        if (!canceled) setObjectUrl(null);
      });

    return () => {
      canceled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [item.id]);

  const kind = useMemo(() => {
    if (item.type.startsWith('image/')) return 'image';
    if (item.type.startsWith('video/')) return 'video';
    if (item.type.startsWith('audio/')) return 'audio';
    return 'other';
  }, [item.type]);

  if (!objectUrl) {
    return getMimeTypeIcon(item.type, typeof width === 'number' ? Math.min(width, 80) : 50);
  }

  if (kind === 'image') {
    return <img src={objectUrl} width={width} height={height} style={{ objectFit: 'contain' }} />;
  }

  if (kind === 'video') {
    return <video src={objectUrl} width={width} height={height} controls={controls} />;
  }

  if (kind === 'audio') {
    return <audio src={objectUrl} controls={controls} style={{ width: typeof width === 'number' ? `${width}px` : width }} />;
  }

  return getMimeTypeIcon(item.type, typeof width === 'number' ? Math.min(width, 80) : 50);
};

export default MediaPreview;
